import { useEffect, useState } from "react"
import {
  useTelemedicineForPediatricsActivationStatus,
  useCreateTelemedicineForPediatricsEligibility,
  useInvalidateTelemedicineForPediatricsEligibility,
  useRefreshTelemedicineForPediatricsEnrollmentUrl
} from "#/components/telemedicine/pediatrics/hooks/useTelemedicineForPediatricsActivation"
import Settings from "#/utils/CarrotConfig"
import { reportError } from "#/utils/ErrorReporting"

export enum AccessBlueberryAccountDialogStep {
  WHICH_EMAIL,
  LEAVING_CARROT,
  ERROR
}

export interface UseAccessBlueberryAccountDialogState {
  step: AccessBlueberryAccountDialogStep
  email: string
  isLoading: boolean
  isError: boolean
  continueUrl: string | null
  isCreatingEligibility: boolean
  goToLeavingCarrot: (email: string) => void
  goToWhichEmail: () => void
  closeAndReset: () => void
}

export function useAccessBlueberryAccountDialog(
  onClose: () => void,
  open: boolean
): UseAccessBlueberryAccountDialogState {
  const { activationStatus, isLoading, isError } = useTelemedicineForPediatricsActivationStatus()
  const invalidateEligibility = useInvalidateTelemedicineForPediatricsEligibility()
  const { mutate: createEligibility, isLoading: isCreatingEligibility } =
    useCreateTelemedicineForPediatricsEligibility()
  const {
    mutate: refreshEnrollmentUrl,
    data: refreshedEligibility,
    reset: resetRefresh
  } = useRefreshTelemedicineForPediatricsEnrollmentUrl()

  const [step, setStep] = useState<AccessBlueberryAccountDialogStep>(AccessBlueberryAccountDialogStep.WHICH_EMAIL)
  const [email, setEmail] = useState<string>("")
  const [createdEnrollmentUrl, setCreatedEnrollmentUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!isLoading && activationStatus !== "not-started") {
      setStep(AccessBlueberryAccountDialogStep.LEAVING_CARROT)
    }
  }, [isLoading, activationStatus])

  useEffect(() => {
    if (open && !isLoading && activationStatus === "in-progress") {
      refreshEnrollmentUrl(undefined, {
        onSuccess: (data) => {
          if (data.registrationCompleted) {
            invalidateEligibility()
          }
        },
        onError: (error) => {
          reportError(error)
          setStep(AccessBlueberryAccountDialogStep.ERROR)
        }
      })
    }
  }, [open, isLoading, activationStatus, refreshEnrollmentUrl, invalidateEligibility])

  const getContinueUrl = (): string | null => {
    if (activationStatus === "activated") {
      return Settings.BLUEBERRY_APP_URL
    }
    if (activationStatus === "in-progress") {
      return refreshedEligibility?.enrollmentUrl ?? null
    }
    return createdEnrollmentUrl
  }

  return {
    step,
    email,
    isLoading,
    isError,
    continueUrl: getContinueUrl(),
    isCreatingEligibility,
    goToLeavingCarrot: (submittedEmail: string) => {
      setEmail(submittedEmail)
      setStep(AccessBlueberryAccountDialogStep.LEAVING_CARROT)
      if (activationStatus === "not-started") {
        createEligibility(submittedEmail, {
          onSuccess: (data) => {
            if (!data.eligibilityRecordId) {
              setStep(AccessBlueberryAccountDialogStep.ERROR)
              return
            }
            setCreatedEnrollmentUrl(data.enrollmentUrl)
          },
          onError: () => {
            setStep(AccessBlueberryAccountDialogStep.ERROR)
          }
        })
      }
    },
    goToWhichEmail: () => setStep(AccessBlueberryAccountDialogStep.WHICH_EMAIL),
    closeAndReset: () => {
      setStep(
        activationStatus !== "not-started"
          ? AccessBlueberryAccountDialogStep.LEAVING_CARROT
          : AccessBlueberryAccountDialogStep.WHICH_EMAIL
      )
      setEmail("")
      setCreatedEnrollmentUrl(null)
      resetRefresh()
      invalidateEligibility()
      onClose()
    }
  }
}
